import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useVerifyOtp, useResendOtp } from '@/shared/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/shared/components/ui';
import { AlertCircle, Mail, ShieldCheck } from 'lucide-react';
import { AxiosError } from 'axios';
import mamLogo from '@/assets/mamLogo.svg';
import { OTP_STRINGS } from '@/shared/constants/strings';
import { otpSchema } from '@/shared/schemas';

const RESEND_COOLDOWN_SECONDS = 60;

/** Ticks the cooldown down by one, clearing `timerRef`'s interval once it hits zero. */
function decrementCooldown(
  prev: number,
  timerRef: MutableRefObject<ReturnType<typeof setInterval> | null>
): number {
  if (prev <= 1) {
    if (timerRef.current) clearInterval(timerRef.current);
    return 0;
  }
  return prev - 1;
}

interface OtpLocationState {
  mfaSessionId: string;
  userEmail: string;
  userName: string;
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain || local.length <= 2) return email;
  return `${local[0]}${'*'.repeat(Math.min(local.length - 2, 4))}${local[local.length - 1]}@${domain}`;
}

export default function VerifyOtp() {
  const location = useLocation();
  const state = location.state as OtpLocationState | null;

  const [otpValue, setOtpValue] = useState('');
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [validationError, setValidationError] = useState<string | null>(null);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initialTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const verifyMutation = useVerifyOtp();
  const resendMutation = useResendOtp();

  useEffect(() => {
    initialTimerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(initialTimerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      // Clear both timers on unmount
      if (initialTimerRef.current) clearInterval(initialTimerRef.current);
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  if (!state?.mfaSessionId) {
    return <Navigate to="/login" replace />;
  }

  const { mfaSessionId, userEmail } = state;
  const isPending = verifyMutation.isPending;
  const isComplete = otpValue.length === 6;

  const handleOtpChange = (value: string) => {
    setOtpValue(value);
    verifyMutation.reset();
    setValidationError(null);

    if (value.length === 6) {
      // Validate OTP against schema before sending
      const validationResult = otpSchema.safeParse(value);
      if (validationResult.success) {
        verifyMutation.mutate({ mfaSessionId, otpCode: value });
      } else {
        // Show validation error
        setValidationError(OTP_STRINGS.ERROR_INVALID);
      }
    }
  };

  const handleVerify = () => {
    if (isComplete && !isPending) {
      verifyMutation.mutate({ mfaSessionId, otpCode: otpValue });
    }
  };

  const handleResend = () => {
    if (cooldown > 0 || resendMutation.isPending) return;
    resendMutation.mutate(
      { mfaSessionId },
      {
        onSuccess: () => {
          setOtpValue('');
          verifyMutation.reset();
          setCooldown(RESEND_COOLDOWN_SECONDS);
          // Clear old timer if exists and start new one
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          cooldownRef.current = setInterval(() => {
            setCooldown((prev) => decrementCooldown(prev, cooldownRef));
          }, 1000);
        },
      }
    );
  };

  const errorMessage = (() => {
    // Client-side validation error takes priority
    if (validationError) {
      return validationError;
    }

    if (!verifyMutation.error) return '';
    const axiosError = verifyMutation.error as AxiosError;

    // Differentiate error types based on HTTP status
    if (axiosError.response?.status === 401) {
      return OTP_STRINGS.ERROR_INVALID; // Invalid or expired OTP
    }

    if (axiosError.response?.status === 429) {
      return OTP_STRINGS.ERROR_LOCKOUT; // Account locked
    }

    // For network errors, timeouts, 5xx, etc.
    return OTP_STRINGS.ERROR_GENERIC;
  })();

  const renderVerifyButtonContent = () => {
    if (isPending) {
      return (
        <span className="flex items-center justify-center gap-2">
          <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          {OTP_STRINGS.LOADING_TEXT}
        </span>
      );
    }
    if (isComplete) {
      return (
        <span className="flex items-center justify-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          {OTP_STRINGS.SUBMIT_BUTTON}
        </span>
      );
    }
    return OTP_STRINGS.SUBMIT_BUTTON;
  };

  const renderResendButtonContent = () => {
    if (resendMutation.isPending) {
      return (
        <>
          <div className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          {OTP_STRINGS.RESEND_BUTTON}…
        </>
      );
    }
    if (cooldown > 0) {
      return OTP_STRINGS.RESEND_COOLDOWN.replace('{seconds}', String(cooldown));
    }
    return OTP_STRINGS.RESEND_BUTTON;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      {/* Card — same dimensions and style as Login */}
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-5 sm:p-8">
        {/* Brand header */}
        <div className="flex justify-center mb-8 sm:mb-12">
          <img
            src={mamLogo}
            alt="MamBaby Logo"
            className="w-12 h-12"
            width="48"
            height="48"
            fetchPriority="high"
          />
        </div>

        {/* Title block with icon */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-primary-soft flex items-center justify-center shrink-0">
            <Mail className="h-7 w-7 text-brand-blue" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground leading-tight">
              {OTP_STRINGS.TITLE}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {OTP_STRINGS.SUBTITLE}
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="text-muted-foreground text-sm mb-8 leading-relaxed">
          {OTP_STRINGS.DESCRIPTION}{' '}
          <span className="font-semibold text-foreground">
            {maskEmail(userEmail)}
          </span>
          . {OTP_STRINGS.DESCRIPTION_SUFFIX}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleVerify();
          }}
          className="space-y-5"
        >
          {/* Error alert */}
          {verifyMutation.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* OTP input — full-width, no red outline */}
          <div className="space-y-2">
            <label
              htmlFor="otp-input"
              className="text-foreground text-sm font-medium"
            >
              {OTP_STRINGS.CODE_LABEL}
            </label>
            <InputOTP
              maxLength={6}
              value={otpValue}
              onChange={handleOtpChange}
              disabled={isPending}
              id="otp-input"
              aria-label={OTP_STRINGS.CODE_LABEL}
              // outline-none removes the browser native focus ring from the hidden <input>
              className="outline-none"
              containerClassName="w-full"
            >
              <InputOTPGroup className="w-full gap-2.5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <InputOTPSlot
                    key={`otp-slot-${i}`}
                    index={i}
                    className="
                      flex-1 min-w-0 h-[58px] rounded-lg border-2 border-border
                      bg-input-muted text-foreground text-xl font-bold
                      transition-all duration-150
                      data-[active=true]:border-brand-blue
                      data-[active=true]:bg-white
                      data-[active=true]:ring-4
                      data-[active=true]:ring-brand-blue/10
                    "
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          {/* Verify button — solid brand-blue (same as Login), disabled only during loading */}
          <Button
            type="submit"
            className="w-full h-12 text-white text-base font-medium rounded-lg bg-brand-blue hover:bg-primary-hover"
            disabled={isPending}
            onClick={handleVerify}
          >
            {renderVerifyButtonContent()}
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-muted-foreground">
                {OTP_STRINGS.RESEND_LABEL}
              </span>
            </div>
          </div>

          {/* Resend button */}
          <div className="text-center -mt-1">
            <button
              type="button"
              onClick={handleResend}
              disabled={cooldown > 0 || resendMutation.isPending || isPending}
              className="
                inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue
                hover:underline underline-offset-2 transition-colors
                disabled:text-muted-foreground disabled:cursor-not-allowed disabled:no-underline
              "
            >
              {renderResendButtonContent()}
            </button>
          </div>

          {/* Back to login */}
          <div className="text-center pt-1">
            <Link
              to="/login"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← {OTP_STRINGS.BACK_TO_LOGIN}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
